import type {
  InvoiceOwnerCompanySuggestionsQueryParams,
  InvoiceOwnerCompanySuggestionsResponse,
} from './types';

import {
  keepPreviousData,
  queryOptions,
  type QueryFunctionContext,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { ms } from 'ms';

import { queryKeysInvoice } from '../queryKeys';

import { fetchInvoiceOwnerCompanySuggestions } from './fetchInvoiceOwnerCompanySuggestions';

/**
 * Generates the query options for the invoice owner company suggestions so
 * they can be shared between the useQuery hook and imperative fetching.
 *
 * @param queryParams - Optional name substring and result limit.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getInvoiceOwnerCompanySuggestionsQueryOptions = <
  TData = InvoiceOwnerCompanySuggestionsResponse,
>({
  queryParams = {},
  options,
}: {
  queryParams?: Partial<InvoiceOwnerCompanySuggestionsQueryParams>;
  options?: Omit<
    UseQueryOptions<InvoiceOwnerCompanySuggestionsResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }: QueryFunctionContext) =>
      fetchInvoiceOwnerCompanySuggestions(queryParams, { signal }),
    queryKey: queryKeysInvoice.getOwnerCompanySuggestions(queryParams),
    staleTime: ms('60s'),
    ...options,
  });

/**
 * React Query hook for the companies the user may set as owner of an imported
 * invoice.
 *
 * @param queryParams - Optional name substring and result limit.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoices/operation/suggest_owner_companies_invoices_suggestions_owner_companies_get
 */
export const useQueryInvoiceOwnerCompanySuggestions = <
  TData = InvoiceOwnerCompanySuggestionsResponse,
>(
  queryParams?: Partial<InvoiceOwnerCompanySuggestionsQueryParams>,
  options?: Omit<
    UseQueryOptions<InvoiceOwnerCompanySuggestionsResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery(
    getInvoiceOwnerCompanySuggestionsQueryOptions<TData>({
      options,
      queryParams,
    }),
  );
